// History snapshots while writing. The composer passes a counter that goes up
// after every successful autosave. The first save of a session starts a
// 10-minute clock; when it runs out, the writing (as saved) is snapshotted as
// 'autosave' if anything was saved since the last snapshot, and the next save
// starts the clock again — so at most one autosave version per 10 minutes, and
// none at all for a session that only reads. Leaving (unmount or `pagehide`)
// takes one more if anything changed since. Failures only warn: History must
// never get in the way of writing. The signature is a contract; keep it.

import { useEffect, useRef } from 'react'
import { useBackend } from './session'

export const AUTO_SNAPSHOT_MS = 10 * 60 * 1000
/**
 * On unmount the composer fires its last autosave without waiting; give it a
 * moment to land so the snapshot (which reads the saved row) includes it.
 */
export const UNMOUNT_SNAPSHOT_DELAY_MS = 1500

export function useAutoSnapshot(writingId: string, savedVersion: number): void {
  const backend = useBackend()
  const backendRef = useRef(backend)
  backendRef.current = backend
  const idRef = useRef(writingId)
  idRef.current = writingId
  /** The latest savedVersion seen. */
  const latest = useRef(savedVersion)
  latest.current = savedVersion
  /** savedVersion at the last snapshot (or when this writing was opened). */
  const snapped = useRef(savedVersion)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const snapshot = useRef((id: string) => {
    backendRef.current.snapshotWriting(id, 'autosave').catch((e: unknown) => {
      console.warn('History: could not save an autosave version.', e)
    })
  }).current
  /** Snapshot if anything was saved since the last one. Returns whether it did. */
  const takeIfChanged = useRef((id: string): boolean => {
    if (latest.current === snapped.current) return false
    snapped.current = latest.current
    snapshot(id)
    return true
  }).current

  // A save with no clock running starts one.
  useEffect(() => {
    if (savedVersion === snapped.current || timer.current !== undefined) return
    timer.current = setTimeout(() => {
      timer.current = undefined
      takeIfChanged(idRef.current)
    }, AUTO_SNAPSHOT_MS)
  }, [savedVersion, takeIfChanged])

  // Per writing: a clean start, a snapshot on pagehide, and one on the way out.
  useEffect(() => {
    snapped.current = latest.current
    const onPageHide = () => {
      clearTimeout(timer.current)
      timer.current = undefined
      takeIfChanged(writingId)
    }
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      clearTimeout(timer.current)
      timer.current = undefined
      if (latest.current !== snapped.current) {
        snapped.current = latest.current
        setTimeout(() => snapshot(writingId), UNMOUNT_SNAPSHOT_DELAY_MS)
      }
    }
  }, [writingId, snapshot, takeIfChanged])
}
